import * as React from 'react'
import { ReactNode } from 'react'
import styled from 'styled-components'
import { Fonts, Colors } from '../constants'
import { BodyText } from './core'

/**
 * HTML <table> component.
 *
 * Pass rows={array of data}, cols={array of functions mapping data to React elements}
 *
 * Expands to fit width of parent container.
 */
export default class Table<R> extends React.PureComponent<{
  rows: R[]
  columns: TableColFn<R>[]
  headers?: ReactNode[]
  widths?: (string | null)[]
  alignments?: (string | null)[]
  keyFn?: (arg: any) => string | number
  spaced?: boolean
  style?: React.CSSProperties
  dataRowComponent?: React.ComponentType<any>
  dataCellComponent?: React.ComponentType<any>
  headerRowComponent?: React.ComponentType<any>
  headerCellComponent?: React.ComponentType<any>
}> {
  render() {
    const { headers, widths, alignments, rows, columns, keyFn } = this.props

    const TblDataRow = (this.props.dataRowComponent || TableRow) as
      | React.ComponentClass<any, any>
      | React.FunctionComponent<any>
    const TblHeaderRow = this.props.headerRowComponent || TableHeaderRow
    const TblDataCell = this.props.dataCellComponent || TableTD
    const TblHeaderCell = this.props.headerCellComponent || TableTH

    let Tbl
    if (this.props.spaced) {
      Tbl = TableSpaced
    } else {
      Tbl = TableFixed
    }

    return (
      <Tbl style={this.props.style}>
        {headers && (
          <TableHeader>
            <TblHeaderRow>
              {headers.map((h, i) => {
                const width = widths && widths[i]
                const textAlign = alignments && alignments[i]
                const style: any = { width, textAlign }
                return (
                  <TblHeaderCell key={i} style={style}>
                    {h}
                  </TblHeaderCell>
                )
              })}
            </TblHeaderRow>
          </TableHeader>
        )}
        <tbody>
          {rows.map((row, i) => {
            const key = keyFn ? keyFn(row) : i
            return (
              <TblDataRow key={key} row={row} index={i}>
                {columns.map((colFn, j) => {
                  const width = i === 0 && widths && widths[j]
                  const textAlign = alignments && alignments[j]
                  const style: any = { width, textAlign }
                  return (
                    <TblDataCell key={j} style={style}>
                      {colFn(row, i)}
                    </TblDataCell>
                  )
                })}
              </TblDataRow>
            )
          })}
        </tbody>
      </Tbl>
    )
  }
}

export type TableColFn<R> = (row: R, index: number) => ReactNode

const TableFixed = styled(BodyText.withComponent('table'))`
  table-layout: fixed;
  text-align: left;
  width: 100%;
`

const TableSpaced = styled(TableFixed)`
  & thead tr {
    height: 24px;
  }
  & tbody tr {
    height: 38px;
  }
`

const TableHeader = styled.thead`
  font-family: ${Fonts.sansHeader};
  font-weight: 500;
`

const TableHeaderRow = styled.tr``

const TableRow = styled.tr`
  &:hover {
    background: ${Colors.black};
  }
`

const TableTH = styled.th`
  padding: 2px 8px 2px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:last-child {
    padding: 2px 0;
  }
`

const TableTD = styled.td`
  padding: 1px 8px 1px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:last-child {
    padding: 2px 0;
  }
`
